import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class AwpProductForm extends Component {
  #productData = null;

  connectedCallback() {
    super.connectedCallback();

    const dataScript = this.querySelector('[ref="productData"]');
    if (dataScript) {
      try {
        this.#productData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('AWP Product Form: Failed to parse product data');
      }
    }

    this.#bindSiblingEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindSiblingEvents();
  }

  #onVariantChange = (event) => {
    if (String(event.detail.productId) !== String(this.dataset.productId)) return;

    if (this.refs.variantInput) {
      this.refs.variantInput.value = event.detail.variantId;
    }

    if (this.refs.addToCartBtn) {
      if (!event.detail.available) {
        this.refs.addToCartBtn.disabled = true;
        this.refs.addToCartBtn.textContent = this.refs.addToCartBtn.dataset.soldText || 'Sold Out';
      } else {
        this.refs.addToCartBtn.disabled = false;
        this.refs.addToCartBtn.textContent = this.refs.addToCartBtn.dataset.addText || 'Add To Cart';
      }
    }
  };

  #onSellingPlanChange = (event) => {
    if (String(event.detail.productId) !== String(this.dataset.productId)) return;

    if (this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = event.detail.sellingPlanId || '';
    }
  };

  #bindSiblingEvents() {
    document.addEventListener('awp:variant-change', this.#onVariantChange);
    document.addEventListener('awp:selling-plan-change', this.#onSellingPlanChange);
  }

  #unbindSiblingEvents() {
    document.removeEventListener('awp:variant-change', this.#onVariantChange);
    document.removeEventListener('awp:selling-plan-change', this.#onSellingPlanChange);
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const subscriptionRadio = document.getElementById('purchase-subscription');
    if (subscriptionRadio && subscriptionRadio.checked) return;

    const form = this.querySelector('form');
    if (!form) return;

    const btn = this.refs.addToCartBtn;
    if (btn) {
      btn.disabled = true;
      btn.classList.add('awp_add-to-cart__btn--loading');
      btn.textContent = 'Adding...';
    }

    const formData = new FormData(form);

    if (!formData.get('selling_plan')) {
      formData.delete('selling_plan');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        const didError = !!response.status;

        if (didError) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );
          this.#resetButton(false);
          if (this.refs.liveRegion) {
            this.refs.liveRegion.textContent = response.description || 'Error adding to cart';
          }
        } else {
          if (this.refs.liveRegion) {
            this.refs.liveRegion.textContent = 'Item added to cart';
          }
          this.#triggerRebuy();
          this.#resetButton(true);
        }

        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            this.dispatchEvent(
              new CartAddEvent({}, this.id || '', {
                didError,
                source: 'awp-product-form',
                itemCount: cart.item_count,
                productId: this.dataset.productId,
                sections: didError ? undefined : response.sections,
              })
            );
          });
      })
      .catch((error) => {
        console.error('AWP Product Form: Cart add error', error);
        this.#resetButton(false);
      });
  }

  #triggerRebuy() {
    if (typeof Rebuy !== 'undefined' && Rebuy.Cart && typeof Rebuy.Cart.fetchCart === 'function') {
      Rebuy.Cart.fetchCart();
      setTimeout(() => {
        if (Rebuy.SmartCart && typeof Rebuy.SmartCart.show === 'function') {
          Rebuy.SmartCart.show();
        }
      }, 500);
    }
  }

  #resetButton(success) {
    const btn = this.refs.addToCartBtn;
    if (!btn) return;

    setTimeout(() => {
      btn.disabled = false;
      btn.classList.remove('awp_add-to-cart__btn--loading');
      btn.textContent = btn.dataset.addText || 'Add To Cart';

      if (!this.#productData || !this.#productData.available) {
        btn.disabled = true;
        btn.textContent = btn.dataset.soldText || 'Sold Out';
      }
    }, success ? 1000 : 500);
  }
}

if (!customElements.get('awp-product-form')) {
  customElements.define('awp-product-form', AwpProductForm);
}
